import { Link } from "react-router-dom";
import { testimonials, faqs } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function AboutPage() {
  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <nav className="pt-20 pb-0" aria-label="面包屑导航">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4">
            <ol className="flex items-center gap-2 text-xs text-foreground-600">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><span className="text-foreground-400">/</span></li>
              <li><span className="text-foreground-950 font-medium">关于我们</span></li>
            </ol>
          </div>
        </nav>

        {/* CTA Hero Section */}
        <section className="bg-background-50 py-12" aria-labelledby="about-hero">
          <div className="max-w-[1000px] mx-auto px-4 md:px-6 text-center">
            <div className="relative w-full rounded-2xl overflow-hidden mb-10 h-[280px] md:h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20illustration%20studio%20scene%2C%20creative%20team%20working%20on%20digital%20art%20tablets%2C%20warm%20lighting%2C%20artistic%20atmosphere%2C%20colorful%20character%20designs%20on%20screens%2C%20professional%20yet%20creative%20workspace%2C%20vibrant%20manhwa%20style%2C%20inviting%20warm%20tones&width=1600&height=900&seq=about-team-banner&orientation=landscape"
                alt="韩漫画网团队"
                className="w-full h-full object-cover"
              />
            </div>
            <h1 id="about-hero" className="text-3xl md:text-4xl font-bold text-foreground-950 mb-6 leading-snug" style={{ fontFamily: "var(--font-heading)" }}>
              让每一部 <span className="italic font-light text-foreground-600">韩漫</span> 都触手可及，<br />
              <strong className="font-bold">韩漫画网</strong> — <span className="italic font-light text-foreground-600">下拉式</span> 阅读新体验
            </h1>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-10 py-4 bg-foreground-950 text-background-50 rounded-full text-base font-semibold hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              开始阅读
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line text-lg"></i>
              </div>
            </Link>
          </div>
        </section>

        {/* About Content Section - Left/Right Split */}
        <section className="bg-background-100/70 py-14" aria-labelledby="about-intro">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Left - Brand Intro */}
              <div className="lg:w-[55%]">
                <h2 id="about-intro" className="text-2xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  关于韩漫画网
                </h2>
                <div className="text-sm text-foreground-700 leading-relaxed space-y-4 max-w-xl">
                  <p>
                    韩漫画网成立于2023年，是国内领先的韩漫在线阅读平台。我们专注于为中文用户提供<strong>原汁原味的韩国漫画阅读体验</strong>，
                    所有内容均为无删减完整版，忠实呈现创作者的原意与细节。
                  </p>
                  <p>
                    平台独创的<strong>下拉式阅读模式</strong>，让漫画阅读变得像刷信息流一样流畅自然。无需频繁点击翻页，只需向下滑动，即可连续阅读，
                    沉浸感大幅提升。这是我们对阅读体验的极致追求。
                  </p>
                  <p>
                    目前，韩漫画网已收录超过 <strong>10,000部</strong> 韩漫作品，涵盖奇幻冒险、恋爱浪漫、武侠格斗、悬疑惊悚、都市现实等多种类型，
                    月活跃用户突破 <strong>500万</strong>。我们与多家韩国漫画工作室保持紧密合作，确保更新速度与韩网同步。
                  </p>
                  <p>
                    我们的使命是：<strong>让每一位中文韩漫爱好者，都能以最舒适的方式，享受最纯粹的韩漫魅力。</strong>
                  </p>
                </div>

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500/10 text-accent-700 rounded-full text-sm font-medium hover:bg-accent-500/20 transition-colors whitespace-nowrap mt-6 cursor-pointer"
                >
                  立即阅读
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>

                <div className="flex items-center gap-12 mt-10">
                  <div>
                    <div className="text-4xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      10,000+
                    </div>
                    <div className="text-xs text-foreground-600 mt-1">漫画作品</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      500万+
                    </div>
                    <div className="text-xs text-foreground-600 mt-1">月活用户</div>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      3年
                    </div>
                    <div className="text-xs text-foreground-600 mt-1">运营时间</div>
                  </div>
                </div>
              </div>

              {/* Right - Brand Image */}
              <div className="lg:w-[45%] flex justify-center lg:justify-end">
                <div className="relative w-[280px] h-[392px] rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20art%20illustration%20of%20a%20cozy%20reading%20scene%2C%20person%20sitting%20by%20window%20reading%20on%20tablet%20with%20warm%20lamp%20light%2C%20bookshelves%20background%2C%20comfortable%20atmosphere%2C%20warm%20golden%20tones%2C%20peaceful%20mood%2C%20clean%20illustration%20style&width=800&height=1120&seq=about-brand-image&orientation=portrait"
                    alt="韩漫画网品牌形象"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-background-50/90 backdrop-blur-sm text-primary-500">
                      始于2023
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-12">
                    <p className="text-white text-sm font-medium">韩漫画网</p>
                    <p className="text-white/70 text-xs">韩漫大全下拉式免费</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="bg-background-50 py-14" aria-labelledby="features-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-background-300 rounded-full text-xs font-medium text-foreground-600 mb-4">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shield-star-line text-sm"></i>
                </div>
                平台优势
              </span>
              <h2 id="features-heading" className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                为什么选择韩漫画网
              </h2>
              <p className="text-sm text-foreground-600 mt-3">四大核心优势，打造极致阅读体验</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  icon: "ri-book-read-line",
                  title: "下拉式阅读",
                  desc: "创新连续阅读模式，向下滑动即可无缝衔接，告别繁琐翻页操作",
                },
                {
                  icon: "ri-shield-check-line",
                  title: "无删减内容",
                  desc: "100%原版内容呈现，完整保留作者创作意图与画面细节",
                },
                {
                  icon: "ri-flashlight-line",
                  title: "极速同步更新",
                  desc: "与韩网保持同步，韩方更新24小时内即完成翻译并上线",
                },
                {
                  icon: "ri-smartphone-line",
                  title: "多端适配",
                  desc: "完美适配手机、平板、电脑，随时随地享受阅读乐趣",
                },
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-background-100 rounded-lg p-6 hover:bg-primary-500 group transition-all duration-300 cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200 group-hover:bg-white/20 mb-4 transition-colors">
                    <i className={`${feature.icon} text-xl text-primary-500 group-hover:text-white transition-colors`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-white mb-2 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-foreground-600 group-hover:text-white/80 leading-relaxed transition-colors">
                    {feature.desc}
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-primary-500 group-hover:text-white transition-colors">
                    <span className="text-xs font-medium">了解更多</span>
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-xs"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-background-100/70 py-14" aria-labelledby="testimonials-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block w-2 h-2 rounded-full bg-accent-500 mr-2 align-middle"></span>
              <span className="text-xs font-semibold text-foreground-600 uppercase tracking-wide">用户口碑</span>
              <h2 id="testimonials-heading" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2" style={{ fontFamily: "var(--font-heading)" }}>
                读者真实评价
              </h2>
              <p className="text-sm text-foreground-600 mt-3">来自韩漫爱好者的真诚反馈</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={t.id}
                  className={`bg-background-50 rounded-lg p-5 ${idx === testimonials.length - 1 ? "opacity-70" : ""}`}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < t.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-400"}`}></i>
                      </div>
                    ))}
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-2">五星好评</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-[10px] text-foreground-600">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="bg-background-50 py-14" aria-labelledby="about-faq-heading">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 id="about-faq-heading" className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-background-100 rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">
                    <a href={`#faq-${idx}`} id={`faq-${idx}`}>{faq.question}</a>
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="bg-background-100/70 py-14" aria-labelledby="contact-heading">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 id="contact-heading" className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                联系我们
              </h2>
              <p className="text-sm text-foreground-600 mt-3">有任何问题或合作意向，欢迎随时联系我们</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">商务合作</h3>
                <p className="text-xs text-foreground-600">hello@xxjiaxiang.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100">
                  <i className="ri-customer-service-line text-xl text-accent-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服支持</h3>
                <p className="text-xs text-foreground-600">hello@xxjiaxiang.com</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-secondary-100">
                  <i className="ri-wechat-line text-xl text-secondary-500"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                <p className="text-xs text-foreground-600">韩漫画网官方</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于韩漫画网",
          description: "韩漫画网成立于2023年，是国内领先的韩漫在线阅读平台，提供无删减韩漫下拉式免费阅读服务。收录超过10,000部韩漫作品，月活用户超500万。",
          url: "http://xxjiaxiang.com/about",
          isPartOf: {
            "@type": "WebSite",
            name: "韩漫画网",
            url: "http://xxjiaxiang.com/",
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "http://xxjiaxiang.com/" },
            { "@type": "ListItem", position: 2, name: "关于我们", item: "http://xxjiaxiang.com/about" },
          ],
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.answer,
            },
          })),
        })}
      </script>
    </>
  );
}